import React, { useEffect, useState } from "react";
import { signOut } from "aws-amplify/auth"; 
import { useNavigate } from "react-router-dom";
import { apiGet } from "../utils/api";

const Sidebar = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    fetchFeeds();
    
    // Close sidebar when window is resized to desktop size
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchFeeds = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/getPodcastFeeds');
      setFeeds(response.feeds || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching feeds:", error);
      setError("Failed to load podcasts");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      
      // Clear session storage
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('userId');
      
      setIsAuthenticated(false);
      navigate("/login");
    } catch (error) {
      console.error("Sign-out error:", error);
    }
  };
  
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  const handleNavigation = (path) => {
    navigate(path);
    // Close sidebar after navigation on mobile
    if (window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  };
  
  // Mobile sidebar toggle button
  const SidebarToggle = () => (
    <button 
      className="sidebar-toggle-btn" 
      id="sidebarToggleBtn"
      onClick={toggleSidebar}
      aria-label="Toggle sidebar menu"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className="sidebar-toggle-icon"
      >
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>
  );
  
  return (
    <>
      {/* Mobile sidebar toggle */}
      <div className="sidebar-toggle-container" id="sidebarToggleContainer">
        <SidebarToggle />
      </div>
      
      {/* Mobile backdrop */}
      <div 
        className={`sidebar-backdrop ${isSidebarOpen ? 'sidebar-backdrop-open' : ''}`} 
        id="sidebarBackdrop"
        onClick={toggleSidebar}
      />
      
      <div className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`} id="mainSidebar">
         <div className="sidebar-title" id="sidebarTitle">
          <img src="/Smack.png" alt="Smack Logo" style={{ height: '40px', width: 'auto' }} />
        </div>
        
        <div className="sidebar-main-nav" id="sidebarMainNav">
          <button 
            onClick={() => handleNavigation("/dashboard")}
            className="sidebar-nav-item sidebar-dashboard-btn" 
            id="sidebarDashboardBtn"
          >
            Dashboard
          </button>
          <button 
            onClick={() => handleNavigation("/account-settings")}
            className="sidebar-nav-item sidebar-account-btn" 
            id="sidebarAccountBtn"
          >
            Account Settings
          </button>
          <button 
            onClick={() => handleNavigation("/billing")}
            className="sidebar-nav-item sidebar-billing-btn" 
            id="sidebarBillingBtn"
          >
            Billing
          </button>
          <button 
            onClick={() => handleNavigation("/social-media-manager")}
            className="sidebar-nav-item sidebar-social-btn" 
            id="sidebarSocialBtn"
          >
            Social Media Manager
          </button>
        </div>
        
        <h3 className="sidebar-podcasts-title" id="sidebarPodcastsTitle">Your Podcasts</h3>
        
        <div className="sidebar-podcasts-section" id="sidebarPodcastsSection">
          {loading ? (
            <p className="sidebar-loading-text">Loading podcasts...</p>
          ) : error ? (
            <p className="sidebar-error-text">{error}</p>
          ) : feeds.length > 0 ? (
            <ul className="sidebar-podcasts-list" id="sidebarPodcastsList">
              {feeds.map(feed => (
                <li key={feed.feedId} className="sidebar-podcast-item" id={`sidebarPodcast-${feed.feedId}`}>
                  <button 
                    onClick={() => handleNavigation(`/podcast/${feed.feedId}`)}
                    className="sidebar-podcast-btn"
                  >
                    {feed.feedName || "Unnamed Podcast"}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="sidebar-no-podcasts-text">No podcasts available.</p>
          )}
        </div>

        <div className="sidebar-footer" id="sidebarFooter">
          <button 
            onClick={handleSignOut}
            className="sidebar-signout-btn"
            id="sidebarSignoutBtn"
          >
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;